import { combineReducers } from 'redux-immutable'
import { handleActions, combineActions } from 'redux-actions'
import { Map, Set } from 'immutable'
import add from 'ramda/src/add'
import subtract from 'ramda/src/subtract'
import curry from 'ramda/src/curry'
import __ from 'ramda/src/__'

import { ConversationStatusEnum } from 'constants/ConversationStatusConstants'
import { Whitelabel } from './models'
import { keyBy } from 'utils/immutable'
import * as actions from './actions'

const sub1 = subtract(__, 1)
const add1 = add(1)

const isOpenOrPending = status => {
  return status === ConversationStatusEnum.OPEN.key
    || status === ConversationStatusEnum.PENDING.key
}

const updateParticipantCounts = curry((payload, counts) => {
  if (!counts) return
  const {
    isAdd,
    conversationStatus,
    currentParticipants, // All participants in the conversation after the event
    participants, // Those participants added/removed with this event
    currentUserId,
  } = payload
  if (isOpenOrPending(conversationStatus)) {
    if (participants.includes(currentUserId)) {
      counts = counts.update('participating', isAdd ? add1 : sub1)
    }
    if (!currentParticipants.length && !isAdd) {
      counts = counts.update('noParticipants', add1)
    } else if (isAdd && Set(currentParticipants).union(Set(participants)).subtract(Set(participants)).isEmpty()) {
      counts = counts.update('noParticipants', sub1)
    }
  }
  return counts
})

const statusEnumToStatus = {
  [ConversationStatusEnum.OPEN.key]: 'open',
  [ConversationStatusEnum.PENDING.key]: 'pending',
  [ConversationStatusEnum.CLOSED.key]: 'closed',
}

const updateParticipantCounts2 = curry((payload, counts) => {
  if (!counts) return
  const {
    isAdd,
    conversationStatus,
    currentParticipants,
    participants,
    currentUserId,
  } = payload

  let previousPrimaryKey
  let currentPrimaryKey
  const secondaryKey = statusEnumToStatus[conversationStatus]

  const previousParticipants = isAdd
    ? Set(currentParticipants).subtract(Set(participants))
    : Set(currentParticipants).union(Set(participants))

  if (previousParticipants.isEmpty()) {
    previousPrimaryKey = 'noParticipants'
  } else if (previousParticipants.includes(currentUserId)) {
    previousPrimaryKey = 'participating'
  } else {
    previousPrimaryKey = 'all'
  }

  if (Set(currentParticipants).isEmpty()) {
    currentPrimaryKey = 'noParticipants'
  } else if (Set(currentParticipants).includes(currentUserId)) {
    currentPrimaryKey = 'participating'
  } else {
    currentPrimaryKey = 'all'
  }

  if (previousPrimaryKey === currentPrimaryKey) {
    // No change--no counts to update (assumes conversation status has not changed)
    return counts
  }

  if (previousPrimaryKey !== 'all') {
    counts = counts.updateIn([previousPrimaryKey, secondaryKey], sub1)
  }

  if (currentPrimaryKey !== 'all') {
    counts = counts.updateIn([currentPrimaryKey, secondaryKey], add1)
  }

  return counts
})

const countsById = handleActions({
  [actions.receiveCounts]: (state, action) => {
    return state.set(action.payload.id, action.payload.counts)
  },

  [actions.changeParticipants]: (state, action) => {
    const { inboxId, ...payload } = action.payload
    return state.update(inboxId, updateParticipantCounts(payload))
  },

  [actions.changeStatus]: (state, action) => {
    const {
      id,
      currentParticipants,
      currentUserId,
      previousStatus,
      status,
    } = action.payload

    // these should never be counted (or uncounted)
    if (status === 'BULK_SENT' && !previousStatus) {
      return state
    }

    let newState = state.updateIn([id, status.toLowerCase()], add1)

    // magic. trust us - "trun and tommy"
    if (previousStatus && previousStatus !== 'BULK_SENT') {
      newState = newState.updateIn([id, previousStatus.toLowerCase()], sub1)
    } else {
      newState = newState.updateIn([id, 'all'], add1)
    }

    const previousOpenOrPending = isOpenOrPending(previousStatus)
    const currentOpenOrPending = isOpenOrPending(status)
    if (previousStatus && previousStatus !== 'BULK_SENT' && previousOpenOrPending !== currentOpenOrPending) {
      const fn = currentOpenOrPending ? add1 : sub1
      if (currentParticipants.includes(currentUserId)) {
        newState = newState.updateIn([id, 'participating'], fn)
      }
      if (!currentParticipants.length) {
        newState = newState.updateIn([id, 'noParticipants'], fn)
      }
    }

    return newState
  },

  [actions.bulkChangeStatus]: (state, action) => {
    const {
      id,
      currentUserId,
      conversationDiffs,
    } = action.payload

    return state.withMutations(_state => {
      conversationDiffs.forEach(({
        currentParticipants,
        previousStatus,
        status,
      }) => {
        // these should never be counted (or uncounted)
        if (status === 'BULK_SENT' && !previousStatus) {
          return
        }

        _state.updateIn([id, status.toLowerCase()], add1)

        // magic. trust us - "trun and tommy"
        if (previousStatus && previousStatus !== 'BULK_SENT') {
          _state.updateIn([id, previousStatus.toLowerCase()], sub1)
        } else {
          _state.updateIn([id, 'all'], add1)
        }

        const previousOpenOrPending = isOpenOrPending(previousStatus)
        const currentOpenOrPending = isOpenOrPending(status)
        if (previousStatus && previousStatus !== 'BULK_SENT' && previousOpenOrPending !== currentOpenOrPending) {
          const fn = currentOpenOrPending ? add1 : sub1
          if (currentParticipants.includes(currentUserId)) {
            _state.updateIn([id, 'participating'], fn)
          }
          if (!currentParticipants.length) {
            _state.updateIn([id, 'noParticipants'], fn)
          }
        }
      })
    })
  },
}, Map())

const countsById2 = handleActions({
  [actions.receiveCounts2]: (state, { payload }) => {
    const { id, counts } = payload

    const countsByPrimaryKey = counts.mapEntries(([primaryKey, count]) => {
      return [
        primaryKey,
        Map({
          open: count,
          pending: 0,
          closed: 0,
        }),
      ]
    })

    return state.set(id, countsByPrimaryKey)
  },
  // Move conversation between primary buckets
  [actions.changeParticipants]: (state, action) => {
    const { inboxId, ...payload } = action.payload
    return state.update(inboxId, updateParticipantCounts2(payload))
  },
  // Move conversation between secondary buckets
  [actions.changeStatus]: (state, action) => {
    const {
      id,
      currentParticipants,
      currentUserId,
      previousStatus,
      status,
    } = action.payload

    // BULK_SENT conversations should never be counted (or uncounted; see logic/comments below)
    if (status === 'BULK_SENT' && !previousStatus) {
      return state
    }

    let newState = state

    const subtractSecondaryKey = statusEnumToStatus[previousStatus]
    const addSecondaryKey = statusEnumToStatus[status]

    let addPrimaryKey
    let subtractPrimaryKey

    if (currentParticipants.includes(currentUserId)) {
      addPrimaryKey = 'participating'
    }

    if (!currentParticipants.length) {
      addPrimaryKey = 'noParticipants'
    }

    subtractPrimaryKey = addPrimaryKey

    if (addSecondaryKey) {
      if (addPrimaryKey) {
        newState = newState.updateIn([id, addPrimaryKey, addSecondaryKey], add1)
      }

      newState = newState.updateIn([id, 'all', addSecondaryKey], add1)
    }

    if (subtractSecondaryKey) {
      if (subtractPrimaryKey) {
        newState = newState.updateIn([id, subtractPrimaryKey, subtractSecondaryKey], sub1)
      }

      // There is nothing to subtract for conversations that changed status from
      // BULK_SENT since they were previously not counted (see logic/comments above).
      if (previousStatus && previousStatus !== 'BULK_SENT') {
        newState = newState.updateIn([id, 'all', subtractSecondaryKey], sub1)
      }
    }

    return newState
  },

  [actions.bulkChangeStatus]: (state, action) => {
    const {
      id,
      currentUserId,
      conversationDiffs,
    } = action.payload

    return state.withMutations(_state => {
      conversationDiffs.forEach(({
        currentParticipants,
        previousStatus,
        status,
      }) => {
        // BULK_SENT conversations should never be counted (or uncounted; see logic/comments below)
        if (status === 'BULK_SENT' && !previousStatus) {
          return
        }

        const subtractSecondaryKey = statusEnumToStatus[previousStatus]
        const addSecondaryKey = statusEnumToStatus[status]

        let addPrimaryKey
        let subtractPrimaryKey

        if (currentParticipants.includes(currentUserId)) {
          addPrimaryKey = 'participating'
        }

        if (!currentParticipants.length) {
          addPrimaryKey = 'noParticipants'
        }

        subtractPrimaryKey = addPrimaryKey

        if (addSecondaryKey) {
          if (addPrimaryKey) {
            _state.updateIn([id, addPrimaryKey, addSecondaryKey], add1)
          }

          _state.updateIn([id, 'all', addSecondaryKey], add1)
        }

        if (subtractSecondaryKey) {
          if (subtractPrimaryKey) {
            _state.updateIn([id, subtractPrimaryKey, subtractSecondaryKey], sub1)
          }

          // There is nothing to subtract for conversations that changed status from
          // BULK_SENT since they were previously not counted (see logic/comments above).
          if (previousStatus && previousStatus !== 'BULK_SENT') {
            _state.updateIn([id, 'all', subtractSecondaryKey], sub1)
          }
        }
      })
    })
  },
}, Map())

const countsStatusById = handleActions({
  [combineActions(
    actions.fetchCounts,
    actions.fetchCountsPending,
    actions.fetchCountsFailed,
    actions.receiveCounts,
    actions.receiveCounts2,
  )]: (state, action) => state.set(action.payload.id, action.type),
}, Map())

const byDomain = handleActions({
  [actions.createWhitelabelSuccess]: (state, action) => (
    state
      .setIn([action.payload.get('domain'), 'data'], action.payload)
      .setIn([action.payload.get('domain'), 'data', 'fetchStatus'], actions.fetchWhitelabelSuccess.toString())
  ),

  [combineActions(
    actions.fetchWhitelabel,
    actions.fetchWhitelabelPending,
    actions.fetchWhitelabelCancelled,
    actions.fetchWhitelabelFailed,
    actions.fetchWhitelabelSuccess,
  )]: (state, action) => {
    const { domain } = action.payload
    if (domain === undefined)
      return state
    return state.updateIn([domain, 'data'], Whitelabel({ domain }), (whitelabel) => whitelabel.set('fetchStatus', action.type))
  },

  [actions.fetchWhitelabelSuccess]: (state, action) => state.setIn([action.payload.get('domain'), 'data'], action.payload.set('fetchStatus', action.type)),

  [combineActions(
    actions.verifyWhitelabelFailed,
    actions.verifyWhitelabelSuccess,
  )]: (state, action) => {
    const { domain, whitelabel } = action.payload
    if (domain === undefined)
      return state
    return state.setIn([domain, 'data'], whitelabel.set('verifyStatus', action.type))
  },

  [combineActions(
    actions.verifyWhitelabelPending,
    actions.verifyWhitelabelCancelled,
  )]: (state, action) => {
    const { domain } = action.payload
    if (domain === undefined)
      return state
    return state.updateIn([domain, 'data'], Whitelabel({ domain }), (whitelabel) => whitelabel.set('verifyStatus', action.type))
  },

  [actions.fetchWhitelabelFailed]: (state, action) => {
    const { domain, missing } = action.payload
    if (domain === undefined)
      return state
    return state.updateIn([domain, 'data'], Whitelabel({ domain }), whitelabel => whitelabel.set('missing', missing))
  },
}, Map())

const whitelabelCreatePending = handleActions({
  [combineActions(
    actions.createWhitelabel,
    actions.createWhitelabelPending,
  )]: () => true,

  [combineActions(
    actions.createWhitelabelFailed,
    actions.createWhitelabelCancelled,
    actions.createWhitelabelSuccess,
  )]: () => false,
}, null)

const currentInboxId = handleActions({
  [actions.inboxChanged]: (state, action) => action.payload.inboxId,
}, null)

const sendingDomains = handleActions({
  [combineActions(
    actions.fetchSendingDomains,
    actions.fetchSendingDomainsPending,
  )]: (state, action) => {
    return state.setIn(['status', 'fetch'], action.type)
  },
}, Map())

const byId = handleActions({
  [combineActions(
    actions.receiveInbox, 
    actions.patchInboxSuccess, 
    actions.createInboxSuccess
  )]: (state, action) => {
    const inbox = action.payload
    return state.set(inbox.id, inbox)
  },

  [actions.receiveInboxList]: (state, action) => {
    return keyBy(action.payload)
  },
}, Map())

const status = handleActions({
  [combineActions(
    actions.fetchInboxList,
    actions.fetchInboxListPending,
    actions.fetchInboxListCancelled,
    actions.fetchInboxListFailed,
    actions.receiveInboxList
  )]: (state, action) => {
    return action.type
  },
}, null)

const createdInboxId = handleActions({
  [actions.createInboxSuccess]: (state, action) => {
    const inbox = action.payload
    return inbox.id
  },
}, false)

export default combineReducers({
  countsById,
  countsById2,
  countsStatusById,
  byDomain,
  whitelabelCreatePending,
  currentInboxId,
  sendingDomains,
  byId,
  status,
  createdInboxId
})



// WEBPACK FOOTER //
// ./src/scripts/app/modules/inbox/reducer.js